'use client';

import { useState } from 'react';
import { FEATURE_FLAGS, FeatureFlag } from '@config/featureFlags/featureFlags';
import {
  ACTIVE_FEATURE_FLAGS,
  filterFeatureFlags,
  getActiveFeatureFlags,
} from '@config/featureFlags/getActiveFeatureFlags';
import { useClientHasRendered } from '@domain/session/useClientHasRendered';
import { localStorageWrapper } from '@lib/localStorage/localStorageWrapper';
import { ChevronUpIcon } from '@components/Icons/ChevronUpIcon';
import { ChevronDownIcon } from '@components/Icons/ChevronDownIcon';

type RegisteredFlag = keyof typeof FEATURE_FLAGS;
const ALL_FLAGS = Object.keys(FEATURE_FLAGS) as RegisteredFlag[];

// Reload so anything that read its flag at mount picks up the new state.
const writeFlags = (next: FeatureFlag[]): void => {
  localStorageWrapper.setItem(ACTIVE_FEATURE_FLAGS, next);
  window.location.reload();
};

const toggleFlag = (flag: FeatureFlag, activeFlags: FeatureFlag[]): void => {
  writeFlags(
    activeFlags.includes(flag)
      ? activeFlags.filter(f => f !== flag)
      : filterFeatureFlags([...activeFlags, flag]),
  );
};

const clearAll = (): void => writeFlags([]);

/**
 * Dev affordance shown only when at least one feature flag is active in localStorage.
 *
 * Collapsed: shows the first active flag name with a "+N" pill if there are more.
 * Expanded: lists every registered flag with a toggle, plus a "Clear all" button.
 *
 * Has no effect on production behavior: it only reads/writes the same localStorage key
 * users would otherwise edit by hand, and never renders for visitors without an active flag.
 */
export const FeatureFlagNotification = () => {
  const clientHasRendered = useClientHasRendered();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!clientHasRendered) {
    return null;
  }

  const activeFlags = getActiveFeatureFlags();
  if (activeFlags.length === 0) {
    return null;
  }

  const [firstActive, ...rest] = activeFlags;

  if (!isExpanded) {
    return (
      <button
        type="button"
        onClick={() => setIsExpanded(true)}
        className="bg-success text-sea-foam absolute top-0 left-0 z-50 flex items-center gap-1 px-2 py-1 font-mono text-xs hover:opacity-90"
        aria-label="Open feature flags panel"
      >
        <span>{firstActive}</span>
        {rest.length > 0 && (
          <span
            className="bg-sea-foam text-success rounded-full px-1.5 text-[10px] font-semibold"
            aria-label={`${rest.length} more active`}
          >
            +{rest.length}
          </span>
        )}
        <ChevronDownIcon size={14} aria-hidden />
      </button>
    );
  }

  return (
    <div className="absolute top-0 left-0 z-50 w-80 max-w-[95vw] bg-white shadow-lg">
      <button
        type="button"
        onClick={() => setIsExpanded(false)}
        className="bg-success text-sea-foam flex w-full items-center justify-between px-2 py-1 text-xs hover:opacity-90"
        aria-label="Collapse feature flags panel"
      >
        <span className="font-semibold">
          Feature flags ({activeFlags.length} of {ALL_FLAGS.length} active)
        </span>
        <ChevronUpIcon size={16} aria-hidden />
      </button>

      <ul className="text-trench max-h-[60vh] overflow-y-auto bg-white">
        {ALL_FLAGS.map(flag => {
          const meta = FEATURE_FLAGS[flag];
          const isActive = activeFlags.includes(flag);
          const jiraKey = meta.jiraLink.split('/').pop() ?? 'Jira';
          return (
            <li key={flag} className="border-mist flex gap-2 border-b px-2 py-2 last:border-b-0">
              <input
                id={`ff-${flag}`}
                type="checkbox"
                checked={isActive}
                onChange={() => toggleFlag(flag, activeFlags)}
                className="mt-1 shrink-0"
              />
              <label htmlFor={`ff-${flag}`} className="flex min-w-0 flex-col gap-0.5">
                <span className="font-mono text-xs break-all">{flag}</span>
                <span className="text-xs leading-snug">{meta.description}</span>
                <a
                  href={meta.jiraLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary text-xs hover:underline"
                  onClick={e => e.stopPropagation()}
                >
                  {jiraKey}
                </a>
              </label>
            </li>
          );
        })}
      </ul>

      <div className="bg-success text-sea-foam px-2 py-1 text-xs">
        <button type="button" onClick={clearAll} className="hover:underline">
          Clear all flags
        </button>
      </div>
    </div>
  );
};
